import { CategoryFragment } from '@gql/_generated/categories.generated';
import { EntriesFragment } from '@gql/_generated/entries.generated';
import client from '@lib/fetch/client';
import { withSite } from '@lib/hooks';
import { parseApiError } from '@lib/parse/parseApiError';
import { makeNonNullableArray } from '@liquorice/allsorts-craftcms-nextjs';
import { Category, CategoryTypeId, parseCategories } from '../categories/categories';

import { CategoryRelationCriteriaInput } from '@_generated/types';
import { Entry, EntrySectionId, EntryTypeId, isEntry, parseEntries } from '../entries';
import { EntryIndexQueryVariables, getSdk } from './_generated/getEntryIndexData.generated';

import {
  EntryIndexQueryProps,
  EntryIndexQueryResult,
  getEntryIndexData,
  prepareEntryIndexArgs,
} from './getEntryIndexData';

export default async function getEventIndexPerformances<
  E extends EntrySectionId,
  F extends EntryTypeId,
  C extends CategoryTypeId
>(
  maybeArgs: EntryIndexQueryProps<E, F, C>,
  customVariables?: EntryIndexQueryVariables
): Promise<EntryIndexQueryResult<E, F, C>> {
  const { args, variables } = prepareEntryIndexArgs(maybeArgs, customVariables);

  const hasDateQuery = !!(variables.eventDateEnd || variables.eventDateStart);

  if (!hasDateQuery) return await getEntryIndexData(args, variables);

  const performanceVariables: EntryIndexQueryVariables = {
    entrySection: 'eventPerformance',
    entryOrderBy: 'eventDateStart ASC',
    entryLimit: variables.entryLimit,
    entryOffset: variables.entryOffset,
    eventDateEnd: variables.eventDateEnd,
    eventDateStart: variables.eventDateStart,
    entryCategoryQuery: null,
    entryCategoryIds: null,
    relatedToEntries: [
      {
        section: ['event'],
        relatedTo: variables.entryCategoryIds,
        relatedToCategories: variables.entryCategoryQuery as CategoryRelationCriteriaInput[],
      },
    ],
  };

  const sdk = getSdk(client);

  const response = await sdk.entryIndex(withSite(performanceVariables)).catch((err) => {
    console.warn(parseApiError(err));
  });

  const {
    entries: performanceEntries,
    categories: maybeCategories,
    featuredEntries: maybeFeaturedEntries,
    entryCount = 0,
  } = response ?? {};

  const maybeEntries = makeNonNullableArray(performanceEntries).map((entry) => {
    const performance = isEntry(entry, 'eventPerformance') ? entry : null;
    if (!performance) return null;

    const performanceDate = performance.eventDateStart;
    const event = performance.eventSingle[0];

    if (!isEntry(event, 'event')) return null;

    const eventEntry = event as unknown as EntriesFragment;

    return {
      ...eventEntry,
      eventDateStart: performanceDate,
      eventDateEnd: performanceDate,
    };
  });

  // ----------------------------------------------------------------------------------------------
  // ---- Parse the entries ----

  /*
  TODO: the following functions can be restricted by '__typename', but in reality we
  shouldn't have any incorrect types by this point - so we're just using a type assertion.
  */
  const entries = parseEntries(
    maybeEntries as EntriesFragment[] /* , entrySection */
  ) as Entry<E>[];
  const featuredEntries = parseEntries(
    maybeFeaturedEntries as EntriesFragment[] /* , entrySection */
  ) as Maybe<Entry<E>>[];

  // ----------------------------------------------------------------------------------------------
  // ---- Split the categories and parse ----

  const categories = (args.categoryType ?? []).reduce((results, catId) => {
    return {
      ...results,
      [catId]: parseCategories(maybeCategories as CategoryFragment[], catId),
    };
  }, {} as { [P in C]: Category<C>[] });

  const result: EntryIndexQueryResult<E, F, C> = {
    args,
    entries,
    featuredEntries,
    categories,
    entryCount,
    page: args.page ?? 1,
    pages: variables.entryLimit ? Math.ceil(entryCount / variables.entryLimit) : 1,
  };

  return result;
}
